import { useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight, Download, Printer } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'
import { useToast } from '@/hooks/useToast'
import { extractApiError } from '@/lib/api/client'
import {
  downloadLabelsPdf,
  PREVIEW_LIMIT,
  previewLabels,
  type BrowserPrintLabel,
} from '@/lib/browserPrint'

interface BrowserPrintPreviewModalProps {
  open: boolean
  title: string
  labels: BrowserPrintLabel[]
  expectedCount?: number
  isLoading?: boolean
  loadProgress?: { percent: number; done: number; total: number } | null
  isPrinting?: boolean
  onClose: () => void
  onPrint: () => void
}

function pdfFilename(title: string): string {
  const base = title
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
  const stamp = new Date().toISOString().slice(0, 10)
  return `${base || 'labels'}-${stamp}.pdf`
}

function ProgressPanel({
  caption,
  percent,
  detail,
}: {
  caption: string
  percent: number
  detail?: string
}) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 min-h-[360px]">
      <div className="relative w-14 h-14">
        <div className="absolute inset-0 rounded-full border-4 border-blue-100" />
        <div className="absolute inset-0 rounded-full border-4 border-blue-600 border-t-transparent animate-spin" />
      </div>
      <div className="text-center space-y-1">
        <p className="text-sm font-medium text-gray-800">{caption}</p>
        {detail && <p className="text-xs text-gray-500">{detail}</p>}
      </div>
      <p className="text-4xl font-semibold tabular-nums text-blue-600">{percent}%</p>
      <div className="w-full max-w-sm h-2.5 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-blue-600 transition-[width] duration-200 ease-out rounded-full"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  )
}

export function BrowserPrintPreviewModal({
  open,
  title,
  labels,
  expectedCount,
  isLoading,
  loadProgress,
  isPrinting,
  onClose,
  onPrint,
}: BrowserPrintPreviewModalProps) {
  const { toast } = useToast()
  const preview = previewLabels(labels)
  const total = labels.length
  const [selectedIdx, setSelectedIdx] = useState(0)
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false)
  const [pdfProgress, setPdfProgress] = useState(0)
  const [pdfDone, setPdfDone] = useState(0)

  const renderPercent = loadProgress?.percent ?? 0
  const renderDone = loadProgress?.done ?? 0
  const renderTotal = loadProgress?.total ?? expectedCount ?? 0

  useEffect(() => {
    if (open) setSelectedIdx(0)
  }, [open, labels])

  const safeIdx = total > 0 ? Math.min(selectedIdx, total - 1) : 0
  const current = labels[safeIdx]

  const downloadPdf = async () => {
    if (!labels.length) return
    setIsDownloadingPdf(true)
    setPdfProgress(0)
    setPdfDone(0)
    try {
      await downloadLabelsPdf(labels, pdfFilename(title), (done, pdfTotal) => {
        setPdfDone(done)
        setPdfProgress(Math.round((done / pdfTotal) * 100))
      })
      toast.success('PDF загружен')
    } catch (err) {
      toast.error(extractApiError(err))
    } finally {
      setIsDownloadingPdf(false)
      setPdfProgress(0)
      setPdfDone(0)
    }
  }

  const renderDetail =
    renderTotal > 0
      ? `${Math.min(renderDone, renderTotal)} из ${renderTotal} этикеток`
      : undefined

  const pdfDetail =
    isDownloadingPdf && labels.length > 0
      ? `${pdfDone} из ${labels.length} этикеток`
      : undefined

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      className="w-[min(920px,calc(100vw-2rem))] max-w-none overflow-hidden flex flex-col max-h-[min(720px,calc(100vh-2rem))]"
    >
      <Modal.Header onClose={onClose}>{title}</Modal.Header>
      <Modal.Body className="p-0 flex-1 min-h-0 overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_272px] h-full min-h-[420px] divide-y md:divide-y-0 md:divide-x">
          <div className="flex flex-col bg-gray-50 min-h-0 min-w-0">
            {isLoading ? (
              <ProgressPanel
                caption="Рендер этикеток…"
                percent={renderPercent}
                detail={renderDetail}
              />
            ) : isDownloadingPdf ? (
              <ProgressPanel
                caption="Сборка PDF…"
                percent={pdfProgress}
                detail={pdfDetail}
              />
            ) : total === 0 ? (
              <div className="flex flex-1 items-center justify-center py-16">
                <p className="text-sm text-gray-500">Нет этикеток для печати.</p>
              </div>
            ) : (
              <>
                <div className="flex flex-1 items-center justify-center p-5 min-h-0 overflow-auto">
                  {current && (
                    <img
                      src={current.url}
                      alt={current.caption ?? `Этикетка ${safeIdx + 1}`}
                      className="max-w-full max-h-[min(420px,50vh)] object-contain bg-white border border-gray-200 shadow-sm rounded"
                    />
                  )}
                </div>

                {total > 1 && (
                  <div className="shrink-0 border-t border-gray-200 px-4 py-2.5 flex items-center justify-between gap-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={safeIdx <= 0}
                      onClick={() => setSelectedIdx((i) => Math.max(0, i - 1))}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-gray-600 tabular-nums truncate">
                      {safeIdx + 1} / {total}
                      {current?.caption && (
                        <span className="text-gray-400 ml-2">{current.caption}</span>
                      )}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={safeIdx >= total - 1}
                      onClick={() => setSelectedIdx((i) => Math.min(total - 1, i + 1))}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}

                {preview.length > 1 && (
                  <div className="shrink-0 border-t border-gray-200 px-4 py-2.5 overflow-x-auto">
                    <div className="flex gap-2 min-w-min">
                      {preview.map((label, idx) => (
                        <button
                          key={`${label.url}-${idx}`}
                          type="button"
                          onClick={() => setSelectedIdx(idx)}
                          className={`shrink-0 w-14 h-14 rounded border p-1 bg-white transition-colors ${
                            idx === safeIdx
                              ? 'border-blue-500 ring-2 ring-blue-200'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <img src={label.url} alt="" className="w-full h-full object-contain" />
                        </button>
                      ))}
                      {total > PREVIEW_LIMIT && (
                        <div className="shrink-0 w-14 h-14 rounded border border-dashed border-gray-300 flex items-center justify-center text-xs text-gray-400 text-center px-1">
                          +{total - PREVIEW_LIMIT}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="p-4 flex flex-col gap-3 min-h-0 overflow-y-auto bg-white">
            <div>
              <h3 className="font-semibold text-sm text-gray-900">Печать</h3>
              <p className="text-sm text-gray-600 mt-1 leading-snug">
                Системный диалог браузера или выгрузка в PDF.
              </p>
            </div>

            <dl className="space-y-2 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">Этикеток</dt>
                <dd className="font-medium text-gray-900 tabular-nums">
                  {isLoading && renderTotal > 0 ? `${renderDone} / ${renderTotal}` : total}
                </dd>
              </div>
              {!isLoading && total > PREVIEW_LIMIT && (
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-500">В предпросмотре</dt>
                  <dd className="text-gray-700 tabular-nums">{PREVIEW_LIMIT} из {total}</dd>
                </div>
              )}
            </dl>

            <div className="mt-auto space-y-2 pt-1">
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => void downloadPdf()}
                isLoading={isDownloadingPdf}
                disabled={isLoading || isPrinting || total === 0}
              >
                <Download className="w-4 h-4 mr-2" />
                Загрузить PDF
              </Button>
              <Button
                className="w-full"
                onClick={onPrint}
                isLoading={isPrinting}
                disabled={isLoading || isDownloadingPdf || total === 0}
              >
                <Printer className="w-4 h-4 mr-2" />
                Печать
              </Button>
              <Button
                variant="ghost"
                className="w-full"
                disabled={isPrinting || isDownloadingPdf}
                onClick={onClose}
              >
                Отмена
              </Button>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}
